import { ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'

import type { FormErrors } from './useEntityForm'

/**
 * Which sections own a rejected field.
 *
 * A path belongs to the field it starts with: an error keyed
 * `malwareScanConfigurationAttributes.countries` is an error of the block the section
 * lists, and matching only the exact key would miss every nested one. `useEntityForm`
 * also records each issue under its root, so a section listing only top-level fields
 * matches either way — this is the superset, and the one a nested form needs.
 */
function sectionsWithErrors<T extends object>(
  errors: FormErrors<T>,
  sections: Record<string, ReadonlyArray<keyof T>>
): string[] {
  const keys = Object.keys(errors)
  const owns = (field: string) =>
    keys.some((key) => key === field || key.startsWith(`${field}.`))

  return Object.entries(sections)
    .filter(([, fields]) => fields.some((field) => owns(String(field))))
    .map(([section]) => section)
}

/**
 * The open sections of a `Collapse` that holds part of a form.
 *
 * A rejected field inside a folded section is reported and then hidden: the form says
 * "fix this" about a control that is not on screen, and `scrollToFirstError` has
 * nothing visible to scroll to. So a section with an error opens itself.
 *
 * Pass which fields each section holds — only the form knows that — and bind the returned
 * model to the `Collapse`.
 *
 * Sections only ever open, never close: one the reader folded back is left folded while
 * they type, rather than fighting them for the screen.
 *
 * Pressing Save reopens it, and that rests on something easy to break: `validateForm`
 * assigns a fresh errors object on every attempt, so this watcher runs again even when the
 * failures are word for word the previous ones. **Do not "optimise" the errors object into
 * one that is only reassigned when the set of keys changes** — the second press of Save at
 * a folded section would then do nothing at all.
 */
export function useErrorSections<T extends object>(
  errors: MaybeRefOrGetter<FormErrors<T>>,
  sections: Record<string, ReadonlyArray<keyof T>>
) {
  const openSections = ref<string[]>([])

  watch(
    () => toValue(errors),
    (current) => {
      for (const section of sectionsWithErrors(current, sections))
        if (!openSections.value.includes(section))
          openSections.value.push(section)
    },
    { deep: true }
  )

  return openSections
}

/**
 * The same rule for a `Tabs`, where only one section is on screen at a time.
 *
 * A `Collapse` can open a second section and leave the first alone; tabs cannot, so this
 * one *moves* the reader — a stronger thing to do, and therefore on a narrower trigger. It
 * switches only when a complaint appears that was not there a moment ago, or when Save was
 * pressed, which is why it needs `submitAttempts`:
 *
 * - `errors` is rewritten as fields are *corrected* too, and treating that as a fresh
 *   rejection takes the reader off the tab they are typing in, mid-keystroke.
 * - the set of keys does not change when the same save fails twice, so without the counter
 *   the second press leaves the reader on a clean tab watching a button do nothing.
 *
 * It never moves away from a tab that was itself rejected: the reader is already looking at
 * a problem, and the others can wait.
 */
export function useErrorSection<T extends object>(
  errors: MaybeRefOrGetter<FormErrors<T>>,
  sections: Record<string, ReadonlyArray<keyof T>>,
  active: Ref<string>,
  options: {
    /** The sections that exist right now — a tab the format hides is not switched to. */
    available?: MaybeRefOrGetter<readonly string[]>
    /** `useEntityForm`'s counter: every submit is an attempt, failed ones included. */
    submitAttempts?: MaybeRefOrGetter<number>
  } = {}
) {
  let seen: string[] = []

  const show = () => {
    const current = toValue(errors)
    const rejected = sectionsWithErrors(current, sections)
    if (rejected.includes(active.value)) return

    const shown = options.available ? toValue(options.available) : null
    const target = rejected.find((section) => !shown || shown.includes(section))

    if (target) active.value = target
  }

  watch(
    () => toValue(errors),
    (current) => {
      const keys = Object.keys(current)
      const appeared = keys.some((key) => !seen.includes(key))
      seen = keys

      if (appeared) show()
    },
    { deep: true }
  )

  watch(
    () => (options.submitAttempts ? toValue(options.submitAttempts) : 0),
    (attempts, previous) => {
      // The errors of this attempt are written before the counter is read: the watcher
      // above may have moved the reader already, and `show` is a no-op when it did.
      if (attempts > previous) show()
    },
    { flush: 'post' }
  )
}
